<!--
  采购实例列表页
  用途：展示真实采购案例卡片，点击进入案例详情
  用法：路由 `/cases`；支持按类别筛选
-->
<template>
  <div class="cases-page">
    <section class="cases-hero">
      <div class="cases-hero-inner">
        <div class="hero-copy">
          <h1>{{ t('cases.title') }}</h1>
          <p>{{ t('cases.subtitle') }}</p>
        </div>
        <div class="hero-visual">
          <img class="hero-logo" :src="logoUrl" :alt="t('common.brandLogoAlt')" />
        </div>
      </div>
    </section>

    <section class="cases-body">
      <!--
        案例类别筛选
        用途：按 categoryKey 过滤案例列表；“全部”显示所有案例
      -->
      <div class="category-filter" :aria-label="t('cases.filterLabel')">
        <span class="filter-label">{{ t('cases.filterLabel') }}</span>
        <div class="filter-chips">
          <button
            type="button"
            class="filter-chip"
            :class="{ active: activeCategory === 'all' }"
            @click="activeCategory = 'all'"
          >
            {{ t('cases.filterAll') }}
          </button>
          <button
            v-for="item in categoryOptions"
            :key="item.key"
            type="button"
            class="filter-chip"
            :class="{ active: activeCategory === item.key }"
            @click="activeCategory = item.key"
          >
            {{ item.label }}
          </button>
        </div>
      </div>

      <p v-if="loading" class="filter-empty">{{ t('common.loading') }}</p>
      <p v-else-if="error" class="filter-empty">{{ error }}</p>
      <div v-else-if="cases.length" class="cases-grid">
        <article
          v-for="item in cases"
          :key="item.id"
          class="case-card"
          @click="goDetail(item.id)"
        >
          <div class="case-cover-wrap">
            <img class="case-cover" :src="item.cover" :alt="item.title" />
            <span class="case-year">{{ item.year }}</span>
          </div>
          <div class="case-body">
            <span class="case-category">{{ item.category }}</span>
            <h2>{{ item.title }}</h2>
            <p class="case-client">{{ item.client }}</p>
            <p class="case-summary">{{ item.summary }}</p>
            <div class="case-metrics">
              <div>
                <strong>{{ item.metrics.saving }}</strong>
                <span>{{ item.metricLabels.saving }}</span>
              </div>
              <div>
                <strong>{{ item.metrics.units }}</strong>
                <span>{{ item.metricLabels.units }}</span>
              </div>
              <div>
                <strong>{{ item.metrics.leadTime }}</strong>
                <span>{{ item.metricLabels.leadTime }}</span>
              </div>
            </div>
            <span class="case-link">{{ t('cases.viewDetail') }} →</span>
          </div>
        </article>
      </div>
      <p v-else class="filter-empty">{{ t('cases.filterEmpty') }}</p>
    </section>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { fetchCases, fetchCategories } from '@/services/api'
import { categoryLabel, localizeCaseFromApi } from '@/utils/localize'
import logoUrl from '@/assets/logo.png'

const router = useRouter()
const { t, locale } = useI18n()
const activeCategory = ref('all')
const rawCases = ref([])
const categories = ref([])
const loading = ref(true)
const error = ref('')

const categoryOptions = computed(() => {
  void locale.value
  const used = new Set(rawCases.value.map((c) => c.categoryKey).filter(Boolean))
  return categories.value
    .filter((c) => used.has(c.id))
    .map((c) => ({
      key: c.id,
      label: categoryLabel(c, locale.value) || c.id
    }))
})

const cases = computed(() => {
  void locale.value
  const localized = rawCases.value.map((item) =>
    localizeCaseFromApi(item, locale.value, t, categories.value)
  )
  if (activeCategory.value === 'all') return localized
  return localized.filter((item) => item.categoryKey === activeCategory.value)
})

const goDetail = (id) => {
  router.push({ name: 'case-detail', params: { id } })
}

const loadCases = async () => {
  loading.value = true
  error.value = ''
  try {
    const [caseData, categoryData] = await Promise.all([fetchCases(), fetchCategories()])
    rawCases.value = caseData
    categories.value = categoryData
  } catch (e) {
    error.value = e.message || t('common.loadFailed')
  } finally {
    loading.value = false
  }
}

onMounted(loadCases)
</script>

<style scoped>
.cases-page {
  width: 100%;
}

.cases-hero {
  background:
    radial-gradient(circle at top right, rgba(210, 7, 22, 0.12), transparent 42%),
    linear-gradient(180deg, #fff 0%, #fafafa 100%);
  border-bottom: 1px solid var(--color-border);
}

.cases-hero-inner {
  max-width: var(--max-width);
  margin: 0 auto;
  padding: 48px 24px;
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(180px, 0.85fr);
  align-items: center;
  gap: 32px;
  text-align: left;
}

.hero-visual {
  display: flex;
  align-items: center;
  justify-content: center;
}

.hero-logo {
  width: clamp(160px, 28vw, 280px);
  height: auto;
  aspect-ratio: 1;
  object-fit: contain;
  display: block;
}

.cases-hero h1 {
  max-width: 640px;
  font-size: clamp(28px, 4vw, 40px);
  font-weight: 700;
  margin-bottom: 12px;
}

.cases-hero p {
  max-width: 520px;
  color: var(--color-text-secondary);
  font-size: 16px;
  line-height: 1.7;
}

.cases-body {
  max-width: var(--max-width);
  margin: 0 auto;
  padding: 40px 24px 72px;
}

.category-filter {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 16px;
  margin-bottom: 28px;
}

.filter-label {
  font-size: 14px;
  font-weight: 600;
}

.filter-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.filter-chip {
  border: 1px solid var(--color-border);
  background: #fff;
  color: var(--color-text-secondary);
  font-size: 13px;
  font-weight: 500;
  padding: 8px 14px;
  border-radius: 999px;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;
}

.filter-chip:hover {
  border-color: var(--color-primary);
  color: var(--color-primary);
}

.filter-chip.active {
  background: var(--color-primary);
  border-color: var(--color-primary);
  color: #fff;
}

.cases-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px;
}

.case-card {
  border: 1px solid var(--color-border);
  border-radius: 16px;
  overflow: hidden;
  background: #fff;
  cursor: pointer;
  text-align: left;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}

.case-card:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-soft);
}

.case-cover-wrap {
  position: relative;
  aspect-ratio: 16 / 10;
  overflow: hidden;
  background: var(--color-bg-muted);
}

.case-cover {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.35s ease;
}

.case-card:hover .case-cover {
  transform: scale(1.04);
}

.case-year {
  position: absolute;
  top: 12px;
  right: 12px;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(26, 26, 26, 0.72);
  color: #fff;
  font-size: 12px;
  font-weight: 600;
}

.case-body {
  padding: 20px 20px 22px;
}

.case-category {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  background: var(--color-primary-soft);
  color: var(--color-primary);
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 10px;
}

.case-body h2 {
  font-size: 20px;
  font-weight: 700;
  line-height: 1.35;
  margin-bottom: 6px;
}

.case-client {
  font-size: 13px;
  color: var(--color-text-secondary);
  margin-bottom: 10px;
}

.case-summary {
  font-size: 14px;
  color: var(--color-text-secondary);
  line-height: 1.65;
  margin-bottom: 16px;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.case-metrics {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  padding: 12px;
  border-radius: 12px;
  background: var(--color-bg-muted);
  margin-bottom: 14px;
}

.case-metrics strong {
  display: block;
  font-size: 16px;
  font-weight: 700;
  color: var(--color-primary);
  margin-bottom: 2px;
}

.case-metrics span {
  font-size: 11px;
  color: var(--color-text-secondary);
}

.case-link {
  font-size: 14px;
  font-weight: 600;
  color: var(--color-primary);
}

.filter-empty {
  padding: 48px 16px;
  text-align: center;
  color: var(--color-text-secondary);
}

@media (max-width: 860px) {
  .cases-hero-inner {
    grid-template-columns: 1fr;
    gap: 24px;
    padding: 36px 16px;
  }

  .hero-visual {
    order: -1;
    justify-content: flex-start;
  }

  .hero-logo {
    width: clamp(120px, 36vw, 180px);
  }

  .cases-grid {
    grid-template-columns: 1fr;
  }

  .cases-body {
    padding: 28px 16px 56px;
  }
}
</style>
